import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Circle, OrgMember } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import { useFamilyKeys } from '../../context/FamilyKeysContext';
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  listCircles,
  getOrg,
} from '../../api/client';
import { useRefreshFamilyCategories } from '../../hooks/useFamilyCategories';
import { decryptCategoryName, encryptCategoryName } from '../../crypto/categoryName';
import { base64UrlToBytes } from '../../crypto/base64url';
import { Icon } from '../../components/hbs/Icon';
import { CATEGORY_ICONS, categoryIconName } from '../../lib/categoryIcons';
import Button from '../../components/Button';

interface Category {
  categoryId: string;
  name: string;
  icon?: string;
  circleId?: string;
  order: number;
}

const SCOPE_EVERYONE = '';

/** Shared create/edit form: name, icon glyph, and circle scope. */
function CategoryForm({
  circles,
  initial,
  submitLabel,
  saving,
  onSubmit,
  onCancel,
}: {
  circles: Circle[];
  initial?: { name: string; icon?: string; circleId?: string };
  submitLabel: string;
  saving: boolean;
  onSubmit: (values: { name: string; icon: string; circleId: string }) => void;
  onCancel?: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [icon, setIcon] = useState<string>(initial?.icon ?? 'folder');
  const [circleId, setCircleId] = useState<string>(initial?.circleId ?? SCOPE_EVERYONE);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSubmit({ name: name.trim(), icon, circleId });
      }}
      className="flex flex-col gap-3"
    >
      <div>
        <label className="block text-xs font-medium text-hbs-text-mute mb-1">Binder name</label>
        <input
          aria-label="Binder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Home, Finances, Health"
          required
          className="w-full bg-hbs-bg border border-hbs-border rounded-hbs-md px-3 py-2.5 text-sm text-hbs-text placeholder-hbs-text-mute focus:outline-none focus:border-hbs-primary focus:ring-1 focus:ring-hbs-primary transition-colors"
        />
      </div>

      <div>
        <span className="block text-xs font-medium text-hbs-text-mute mb-1">Icon</span>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORY_ICONS.map((name) => (
            <button
              key={name}
              type="button"
              aria-label={`Icon ${name}`}
              aria-pressed={icon === name}
              id={`category-icon-${name}`}
              onClick={() => setIcon(name)}
              className={`p-2 rounded-hbs-md border transition-colors ${
                icon === name
                  ? 'border-hbs-primary text-hbs-primary bg-hbs-primary/10'
                  : 'border-hbs-border text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt'
              }`}
            >
              <Icon name={name} size={18} />
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-hbs-text-mute mb-1">Visible to</label>
        <select
          aria-label="Visible to"
          value={circleId}
          onChange={(e) => setCircleId(e.target.value)}
          className="w-full bg-hbs-bg border border-hbs-border rounded-hbs-md px-3 py-2.5 text-sm text-hbs-text focus:outline-none focus:border-hbs-primary focus:ring-1 focus:ring-hbs-primary transition-colors"
        >
          <option value={SCOPE_EVERYONE}>Everyone in the family</option>
          {circles.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2">
        <Button type="submit" loading={saving}>{submitLabel}</Button>
        {onCancel && (
          <button
            type="button"
            id="category-form-cancel-button"
          onClick={onCancel}
            className="px-3 py-1.5 text-sm text-hbs-text-mute hover:text-hbs-text rounded-hbs-md transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * "Visible to …" line under a category. When the category is scoped to a
 * circle, the circle name is a hover/focus target that reveals the circle's
 * members, so an admin can see who a scope actually reaches without leaving the
 * page. Whole-family categories render plain text (no members to disambiguate).
 */
function VisibilityLabel({
  circleId,
  circleName,
  memberEmails,
}: {
  circleId?: string;
  circleName: string;
  memberEmails: string[];
}): JSX.Element {
  if (!circleId) {
    return <p className="text-xs text-hbs-text-mute">Visible to {circleName}</p>;
  }
  return (
    <p className="text-xs text-hbs-text-mute">
      Visible to{' '}
      <span className="relative inline-block group/circle">
        <span
          tabIndex={0}
          className="underline decoration-dotted underline-offset-2 cursor-help text-hbs-text-mute hover:text-hbs-text focus:text-hbs-text focus:outline-none rounded-sm"
        >
          {circleName}
        </span>
        <span
          role="tooltip"
          className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden min-w-[10rem] max-w-xs rounded-hbs-md border border-hbs-border bg-hbs-surface-alt p-2 text-left shadow-lg group-hover/circle:block group-focus-within/circle:block"
        >
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-hbs-text-mute mb-1">
            {circleName} members
          </span>
          {memberEmails.length === 0 ? (
            <span className="block text-xs text-hbs-text-mute">No members yet</span>
          ) : (
            <span className="flex flex-col gap-0.5">
              {memberEmails.map((email) => (
                <span key={email} className="block text-xs text-hbs-text">{email}</span>
              ))}
            </span>
          )}
        </span>
      </span>
    </p>
  );
}

export function CategoriesTab(): JSX.Element {
  const { orgId } = useParams<{ orgId: string }>();
  const { orgs, restampCategory } = useSafe();
  const { getKey } = useFamilyKeys();
  const refreshFamilyCategories = useRefreshFamilyCategories();
  const org = orgs.find((o) => o.id === orgId);
  const isAdmin = org?.role === 'admin';

  const [categories, setCategories] = useState<Category[]>([]);
  const [circles, setCircles] = useState<Circle[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const circleName = (id?: string): string =>
    id ? circles.find((c) => c.id === id)?.name ?? 'a circle' : 'Everyone';

  // Emails of members who can see a circle-scoped category — the members whose
  // circle membership includes this circle. Used to populate the scope tooltip.
  const circleMemberEmails = (id?: string): string[] =>
    id ? members.filter((m) => m.circleIds?.includes(id)).map((m) => m.email) : [];

  const reload = useCallback(async () => {
    if (!orgId || !org?.publicKey) return;
    setLoading(true);
    setError('');
    try {
      const [catRes, circleRes, orgRes] = await Promise.all([
        listCategories(orgId),
        listCircles(orgId),
        getOrg(orgId),
      ]);
      if (circleRes.ok) setCircles(circleRes.data.circles);
      if (orgRes.ok) setMembers(orgRes.data.members);
      if (!catRes.ok) {
        setError(catRes.error);
        return;
      }
      const privKey = getKey(orgId);
      const pubBytes = base64UrlToBytes(org.publicKey);
      const decrypted: Category[] = [];
      for (const summary of catRes.data.categories) {
        let name = '(unable to decrypt)';
        let icon: string | undefined;
        if (privKey) {
          try {
            const d = await decryptCategoryName(summary, privKey, pubBytes);
            name = d.name;
            icon = d.icon;
          } catch {
            /* keep the fallback label */
          }
        }
        decrypted.push({ categoryId: summary.categoryId, name, icon, circleId: summary.circleId, order: summary.order });
      }
      decrypted.sort((a, b) => a.order - b.order);
      setCategories(decrypted);
    } finally {
      setLoading(false);
    }
  }, [orgId, org?.publicKey, getKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleCreate(values: { name: string; icon: string; circleId: string }): Promise<void> {
    if (!orgId || !org?.publicKey) return;
    setCreating(true);
    try {
      const blob = await encryptCategoryName(values.name, values.icon, base64UrlToBytes(org.publicKey));
      const nextOrder = categories.reduce((max, c) => Math.max(max, c.order), -1) + 1;
      const res = await createCategory(orgId, {
        ...blob,
        order: nextOrder,
        ...(values.circleId ? { circleId: values.circleId } : {}),
      });
      if (res.ok) {
        await reload();
        refreshFamilyCategories(orgId);
      } else setError(res.error);
    } finally {
      setCreating(false);
    }
  }

  async function handleEditSave(cat: Category, values: { name: string; icon: string; circleId: string }): Promise<void> {
    if (!orgId || !org?.publicKey) return;
    setSavingId(cat.categoryId);
    try {
      const blob = await encryptCategoryName(values.name, values.icon, base64UrlToBytes(org.publicKey));
      const newCircleId = values.circleId || undefined; // '' → whole family
      const res = await updateCategory(orgId, cat.categoryId, {
        ...blob,
        circleId: newCircleId ?? null, // null → explicitly clear scope
      });
      if (res.ok) {
        // Denormalize the new scope onto every item filed under this category
        // so the server's circle read-filter reflects the change. Only when the
        // scope actually changed — a rename/icon edit leaves items untouched.
        if ((cat.circleId ?? undefined) !== newCircleId) {
          restampCategory(orgId, cat.categoryId, newCircleId);
        }
        setEditingId(null);
        await reload();
        refreshFamilyCategories(orgId);
      } else {
        setError(res.error);
      }
    } finally {
      setSavingId(null);
    }
  }

  async function handleMove(index: number, dir: -1 | 1): Promise<void> {
    if (!orgId) return;
    const other = index + dir;
    if (other < 0 || other >= categories.length) return;
    const a = categories[index];
    const b = categories[other];
    setBusyId(a.categoryId);
    try {
      // Swap the two `order` values. Two independent PATCHes; the list reloads
      // from the server afterwards so local order always reflects the truth.
      await Promise.all([
        updateCategory(orgId, a.categoryId, { order: b.order }),
        updateCategory(orgId, b.categoryId, { order: a.order }),
      ]);
      await reload();
      refreshFamilyCategories(orgId);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(categoryId: string): Promise<void> {
    if (!orgId) return;
    setBusyId(categoryId);
    try {
      const res = await deleteCategory(orgId, categoryId);
      if (res.ok) {
        setConfirmDeleteId(null);
        await reload();
        refreshFamilyCategories(orgId);
      } else {
        setError(res.error);
      }
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="text-hbs-text-mute text-sm">Loading…</div>;
  if (error) return <div className="text-hbs-text-mute text-sm">{error}</div>;

  // ── Non-admin: read-only list ───────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">Binders</h2>
        {categories.length === 0 ? (
          <p className="text-sm text-hbs-text-mute">No binders in this family yet.</p>
        ) : (
          <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border overflow-hidden">
            {categories.map((cat, idx) => (
              <div
                key={cat.categoryId}
                className={`flex items-center gap-3 px-4 py-3 ${idx < categories.length - 1 ? 'border-b border-hbs-border' : ''}`}
              >
                <Icon name={categoryIconName(cat.icon)} size={18} className="text-hbs-text-mute" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-hbs-text">{cat.name}</p>
                  <VisibilityLabel
                    circleId={cat.circleId}
                    circleName={circleName(cat.circleId)}
                    memberEmails={circleMemberEmails(cat.circleId)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  // ── Admin: full management ──────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">Binders</h2>
        <p className="text-xs text-hbs-text-mute mb-3">
          Topics for family knowledge (contacts, documents, schedules). Binder names are end-to-end encrypted — the server never sees them. Order here sets the order in the family sidebar.
        </p>
        {categories.length === 0 ? (
          <p className="text-sm text-hbs-text-mute mb-3">No binders yet. Create one below.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {categories.map((cat, idx) => {
              const iconName = categoryIconName(cat.icon);
              const isEditing = editingId === cat.categoryId;
              const isConfirmingDelete = confirmDeleteId === cat.categoryId;
              const rowBusy = busyId === cat.categoryId;

              return (
                <div key={cat.categoryId} className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-4">
                  {isEditing ? (
                    <CategoryForm
                      circles={circles}
                      initial={{ name: cat.name, icon: cat.icon, circleId: cat.circleId }}
                      submitLabel="Save"
                      saving={savingId === cat.categoryId}
                      onSubmit={(values) => void handleEditSave(cat, values)}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <Icon name={iconName} size={18} className="text-hbs-text-mute flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-hbs-text">{cat.name}</p>
                          <VisibilityLabel
                            circleId={cat.circleId}
                            circleName={circleName(cat.circleId)}
                            memberEmails={circleMemberEmails(cat.circleId)}
                          />
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            aria-label={`Move ${cat.name} up`}
                            disabled={idx === 0 || rowBusy}
                            id={`category-move-up-${cat.categoryId}`}
                    onClick={() => void handleMove(idx, -1)}
                            className="px-2 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors disabled:opacity-30"
                          >
                            ↑
                          </button>
                          <button
                            aria-label={`Move ${cat.name} down`}
                            disabled={idx === categories.length - 1 || rowBusy}
                            id={`category-move-down-${cat.categoryId}`}
                    onClick={() => void handleMove(idx, 1)}
                            className="px-2 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors disabled:opacity-30"
                          >
                            ↓
                          </button>
                          <button
                            id={`category-edit-${cat.categoryId}`}
                    onClick={() => setEditingId(cat.categoryId)}
                            className="px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            id={`category-delete-${cat.categoryId}`}
                    onClick={() => setConfirmDeleteId(cat.categoryId)}
                            className="px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-danger hover:bg-hbs-danger/10 rounded-hbs-md transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {isConfirmingDelete && (
                        <div className="mt-3 p-3 bg-hbs-danger/10 border border-hbs-danger/20 rounded-hbs-md">
                          <p className="text-sm text-hbs-text mb-2">
                            Delete “{cat.name}”? Items filed here aren’t deleted — they move out of any binder and keep their current visibility.
                          </p>
                          <div className="flex gap-2">
                            <button
                              id={`category-delete-confirm-${cat.categoryId}`}
                      onClick={() => void handleDelete(cat.categoryId)}
                              disabled={rowBusy}
                              className="px-2.5 py-1.5 text-xs bg-hbs-danger/20 hover:bg-hbs-danger/30 text-hbs-danger rounded-hbs-md transition-colors disabled:opacity-50"
                            >
                              {rowBusy ? '…' : 'Confirm delete'}
                            </button>
                            <button
                              id={`category-delete-cancel-${cat.categoryId}`}
                      onClick={() => setConfirmDeleteId(null)}
                              className="px-2.5 py-1.5 text-xs bg-hbs-surface-alt hover:bg-hbs-surface-alt text-hbs-text rounded-hbs-md transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">Create binder</h2>
        <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-4">
          <CategoryForm
            circles={circles}
            submitLabel="Create binder"
            saving={creating}
            onSubmit={(values) => void handleCreate(values)}
          />
        </div>
      </section>
    </div>
  );
}
